import React, { useState, useMemo, useEffect, useRef } from 'react';
import Footer from '../components/Footer';
import InvoiceCard from '../components/InvoiceCard';
import InvoiceUpload from '../components/InvoiceUpload';
import RecurringExpenseList from '../components/RecurringExpenseList';
import InvoiceViewModal from '../components/InvoiceViewModal';
import Select from '../components/Select';
import SearchInput from '../components/SearchInput';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import { creditService } from '../services/api';
import { formatCurrency, numberToWords } from '../utils/formatCurrency';

const ITEMS_PER_PAGE = 20;

function checkDuplicate(a, b) {
  const vendorA = (a.vendorName || '').toLowerCase().trim();
  const vendorB = (b.vendorName || '').toLowerCase().trim();
  const sameVendor = vendorA === vendorB;
  const amtA = parseFloat(a.amount) || 0;
  const amtB = parseFloat(b.amount) || 0;
  const sameAmount = Math.abs(amtA - amtB) < 0.01;
  const closeAmount = amtA > 0 && amtB > 0 && Math.abs(amtA - amtB) / Math.max(amtA, amtB) < 0.05;
  const sameDate = a.invoiceDate === b.invoiceDate;

  if (sameVendor && sameAmount && sameDate)
    return { confidence: 'high', reason: 'Same vendor, amount & date' };
  if (sameVendor && sameDate && closeAmount)
    return { confidence: 'medium', reason: 'Same vendor & date, amount within 5%' };
  return null;
}

function findDuplicateGroups(invoices) {
  const groups = [];
  const usedIds = new Set();
  for (let i = 0; i < invoices.length; i++) {
    const a = invoices[i];
    if (usedIds.has(a.id)) continue;
    const matches = [];
    for (let j = i + 1; j < invoices.length; j++) {
      const b = invoices[j];
      if (usedIds.has(b.id)) continue;
      const result = checkDuplicate(a, b);
      if (result) matches.push({ invoice: b, ...result });
    }
    if (matches.length > 0) {
      usedIds.add(a.id);
      matches.forEach(m => usedIds.add(m.invoice.id));
      groups.push({ primary: a, matches, confidence: matches[0].confidence, reason: matches[0].reason });
    }
  }
  return groups;
}

const Dashboard = () => {
  const { invoices, allInvoices, loading, filters, setFilters, sortConfig, setSortConfig, deleteInvoice, canDelete, hasPermission, currentUser, loadInvoices } = useInvoice();
  const recurringListRef = useRef(null);
  const confirm = useConfirm();
  const [currentPage, setCurrentPage] = useState(1);
  const [creditBalance, setCreditBalance] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const yearOptions = useMemo(() => {
    const years = new Set();
    allInvoices.forEach(inv => {
      if (inv.invoiceDate) {
        years.add(inv.invoiceDate.substring(0, 4));
      }
    });
    return [...years].sort().reverse();
  }, [allInvoices]);

  const availableMonths = useMemo(() => {
    if (!filters.year) return new Set();
    const months = new Set();
    allInvoices.forEach(inv => {
      if (inv.invoiceDate && inv.invoiceDate.startsWith(filters.year)) {
        months.add(inv.invoiceDate.substring(5, 7));
      }
    });
    return months;
  }, [allInvoices, filters.year]);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchCreditBalance = () => {
    if (currentUser?.id) {
      creditService.getTransactions(currentUser.id)
        .then(data => { if (data.balance !== undefined) setCreditBalance(parseFloat(data.balance)); })
        .catch(() => {});
    }
  };

  useEffect(() => { fetchCreditBalance(); }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch after upload modal closes so credit pill reflects any deduction
  useEffect(() => {
    if (!showUploadModal) fetchCreditBalance();
  }, [showUploadModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when any invoice is updated (covers edits that change credit usage)
  useEffect(() => { fetchCreditBalance(); }, [allInvoices]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'year') {
      setFilters({ ...filters, year: value, month: '' });
    } else {
      setFilters({ ...filters, [name]: value });
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortConfig]);

  const totalPages = Math.ceil(invoices.length / ITEMS_PER_PAGE);
  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return invoices.slice(start, start + ITEMS_PER_PAGE);
  }, [invoices, currentPage]);

  const duplicateGroups = useMemo(() => findDuplicateGroups(allInvoices), [allInvoices]);

  const now = new Date();
  const currentYear = String(now.getFullYear());
  const currentMonth = String(now.getMonth() + 1).padStart(2, '0');

  const hasActiveFilters = filters.search || filters.startDate || filters.endDate
    || filters.year !== currentYear || filters.month !== currentMonth;

  const clearFilters = () => {
    setFilters({ vendor: '', startDate: '', endDate: '', search: '', year: currentYear, month: currentMonth });
  };

  const handleSort = (column) => {
    if (sortConfig.column === column) {
      // Toggle direction if same column
      setSortConfig({
        column,
        direction: sortConfig.direction === 'ASC' ? 'DESC' : 'ASC'
      });
    } else {
      // New column, default to DESC
      setSortConfig({
        column,
        direction: 'DESC'
      });
    }
  };

  const getSortIndicator = (column) => {
    if (sortConfig.column !== column) return null;
    return sortConfig.direction === 'ASC' ? ' ↑' : ' ↓';
  };

  return (
    <>
    <div className="main-content">
        <div className="container">
          <div className="flex-between mb-lg">
            <h1>Office Expenses</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
              {creditBalance !== null && creditBalance > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: '6px',
                  padding: '5px 12px', borderRadius: 'var(--radius-sm)',
                  backgroundColor: 'rgba(22,163,74,0.08)',
                  border: '1px solid rgba(22,163,74,0.25)'
                }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: '#15803d', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Credit</span>
                  <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: '#16a34a' }}>₹{creditBalance.toFixed(2)}</span>
                </div>
              )}
              {hasPermission('create_invoice') && (
                <button onClick={() => setShowUploadModal(true)} className="btn btn-primary">
                  + Add Expense
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '2px', marginBottom: 'var(--spacing-lg)', borderBottom: '2px solid var(--color-gray-200)' }}>
            <button
              onClick={() => setActiveTab('all')}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'all' ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'all' ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'all' ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)' }}
            >
              All Expenses
            </button>
            <button
              onClick={() => setActiveTab('duplicates')}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'duplicates' ? 'var(--font-weight-semibold)' : 'normal', color: activeTab === 'duplicates' ? 'var(--color-primary)' : 'var(--color-secondary)', borderBottom: activeTab === 'duplicates' ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: '-2px', fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              Review Expenses
              {duplicateGroups.length > 0 && (
                <span style={{ background: 'var(--color-primary)', color: '#fff', borderRadius: '2px', padding: '1px 7px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)' }}>
                  {duplicateGroups.length}
                </span>
              )}
            </button>
          </div>

          {/* Recurring expense definitions (collapsible); processed entries appear in the list below */}
          {activeTab === 'all' && <RecurringExpenseList ref={recurringListRef} />}

          {/* Duplicates Tab */}
          {activeTab === 'duplicates' && (
            <div>
              {duplicateGroups.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">✓</div>
                  <div className="empty-state-text">No duplicate entries found</div>
                </div>
              ) : (
                <>
                  <div className="text-secondary mb-md" style={{ fontSize: 'var(--font-size-sm)' }}>
                    {duplicateGroups.length} potential duplicate group{duplicateGroups.length !== 1 ? 's' : ''} detected. Review and remove any unintended entries.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                    {duplicateGroups.map((group, gi) => (
                      <div key={gi} className="card" style={{ borderLeft: `4px solid ${group.confidence === 'high' ? 'var(--color-primary)' : '#f59e0b'}`, padding: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--spacing-sm) var(--spacing-md)', backgroundColor: group.confidence === 'high' ? 'rgba(183,28,28,0.08)' : 'rgba(245,158,11,0.08)', borderBottom: '1px solid var(--color-gray-200)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                            <span style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', padding: '2px 8px', borderRadius: '2px', background: group.confidence === 'high' ? 'var(--color-primary)' : '#f59e0b', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {group.confidence === 'high' ? 'Exact Match' : 'Close Match'}
                            </span>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>{group.reason}</span>
                          </div>
                          <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                            {1 + group.matches.length} entries
                          </span>
                        </div>
                        <div style={{ overflow: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                                <th style={{ padding: 'var(--spacing-xs) var(--spacing-md)', textAlign: 'left', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                                <th style={{ padding: 'var(--spacing-xs) var(--spacing-md)', textAlign: 'left', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Vendor</th>
                                <th style={{ padding: 'var(--spacing-xs) var(--spacing-md)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Amount</th>
                                <th style={{ padding: 'var(--spacing-xs) var(--spacing-md)', textAlign: 'left', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Notes</th>
                                <th style={{ padding: 'var(--spacing-xs) var(--spacing-md)', textAlign: 'center', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Attachment</th>
                                <th style={{ padding: 'var(--spacing-xs) var(--spacing-md)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[group.primary, ...group.matches.map(m => m.invoice)].map((inv, ii) => (
                                <tr key={inv.id} style={{ borderTop: '1px solid var(--color-gray-100)', backgroundColor: ii === 0 ? 'transparent' : 'rgba(245,158,11,0.05)' }}>
                                  <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)' }}>{new Date(inv.invoiceDate).toLocaleDateString('en-GB')}</td>
                                  <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)', fontWeight: 'var(--font-weight-semibold)' }}>{inv.vendorName}</td>
                                  <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)', textAlign: 'right', color: 'var(--color-primary)', fontWeight: 'var(--font-weight-semibold)' }}>{formatCurrency(inv.amount)}</td>
                                  <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)', color: 'var(--color-secondary)' }}>{inv.notes || '—'}</td>
                                  <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)', textAlign: 'center' }}>
                                    {inv.filename ? (
                                      <button
                                        onClick={() => setViewingInvoice(inv)}
                                        className="btn btn-secondary btn-sm"
                                        style={{ fontSize: '11px', padding: '2px 8px' }}
                                      >
                                        View
                                      </button>
                                    ) : (
                                      <span style={{ color: 'var(--color-gray-300)', fontSize: 'var(--font-size-sm)' }}>None</span>
                                    )}
                                  </td>
                                  <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)', textAlign: 'right' }}>
                                    {canDelete(inv) && (
                                      <button
                                        onClick={async () => {
                                          const ok = await confirm(`Delete this ${inv.vendorName} entry for ${formatCurrency(inv.amount)}? This cannot be undone.`, { title: 'Delete Entry', confirmLabel: 'Delete', danger: true });
                                          if (!ok) return;
                                          setDeletingId(inv.id);
                                          await deleteInvoice(inv.id);
                                          setDeletingId(null);
                                        }}
                                        className="btn btn-sm"
                                        disabled={deletingId === inv.id}
                                        style={{ fontSize: '11px', padding: '2px 8px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', opacity: deletingId === inv.id ? 0.6 : 1 }}
                                      >
                                        {deletingId === inv.id ? '…' : 'Delete'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Filters */}
          {activeTab === 'all' && <>

          <div className="card mb-lg">
            <div className="flex gap-md" style={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
              <div style={{ flex: '1 1 300px', display: 'flex' }}>
                <SearchInput
                  placeholder="Search vendor, notes, amount, uploaded by..."
                  value={filters.search}
                  onChange={v => setFilters(f => ({ ...f, search: v }))}
                  inputStyle={{ height: '100%' }}
                />
              </div>

              <div style={{ flex: '0 1 auto', display: 'flex' }}>
                <Select
                  value={filters.year}
                  onChange={v => setFilters({ ...filters, year: v, month: '' })}
                  options={[{ value: '', label: 'All Years' }, ...yearOptions.map(y => ({ value: y, label: String(y) }))]}
                  style={{ minWidth: '100px' }}
                />
              </div>

              <div style={{ flex: '0 1 auto', display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-sm)', padding: '0 var(--spacing-xs)' }}>
                <input
                  type="date"
                  name="startDate"
                  style={{ border: 'none', padding: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
                <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>to</span>
                <input
                  type="date"
                  name="endDate"
                  style={{ border: 'none', padding: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </div>

              <button onClick={clearFilters} className="btn btn-secondary btn-sm" disabled={!hasActiveFilters} style={{ alignSelf: 'stretch' }}>
                Clear Filters
              </button>
            </div>

            {/* Month chips */}
            {filters.year && availableMonths.size > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-xs)', marginTop: 'var(--spacing-md)' }}>
              <button
                onClick={() => setFilters({ ...filters, month: '' })}
                className={`month-chip${!filters.month ? ' month-chip-active' : ''}`}
              >
                All
              </button>
              {MONTH_LABELS.map((label, i) => {
                const value = String(i + 1).padStart(2, '0');
                if (!availableMonths.has(value)) return null;
                return (
                  <button
                    key={value}
                    onClick={() => setFilters({ ...filters, month: value })}
                    className={`month-chip${filters.month === value ? ' month-chip-active' : ''}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            )}
          </div>

          {/* Invoices List — All tab */}
          {loading ? (
            <div className="flex-center" style={{ minHeight: '300px' }}>
              <div className="spinner"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📄</div>
              <div className="empty-state-text">No invoices found</div>
              {hasPermission('create_invoice') && (
                <button onClick={() => setShowUploadModal(true)} className="btn btn-primary mt-md">
                  Add Your First Expense
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex-between mb-md" style={{ alignItems: 'center' }}>
                <span className="text-secondary">
                  Found {invoices.length} invoice{invoices.length !== 1 && 's'}
                </span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)' }}>
                    Total Expenses: {formatCurrency(
                      invoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0)
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-secondary)', fontStyle: 'italic', marginTop: '2px' }}>
                    {numberToWords(
                      invoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0)
                    )}
                  </div>
                </div>
              </div>
              <div className="card" style={{ overflow: 'auto' }}>
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                      <th
                        onClick={() => handleSort('invoiceDate')}
                        style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Date{getSortIndicator('invoiceDate')}
                      </th>
                      <th
                        onClick={() => handleSort('vendorName')}
                        style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Vendor{getSortIndicator('vendorName')}
                      </th>
                      <th
                        onClick={() => handleSort('amount')}
                        style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Amount{getSortIndicator('amount')}
                      </th>
                      <th
                        onClick={() => handleSort('notes')}
                        style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Notes{getSortIndicator('notes')}
                      </th>
                      <th
                        onClick={() => handleSort('uploadedBy')}
                        style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Uploaded By{getSortIndicator('uploadedBy')}
                      </th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInvoices.map(invoice => (
                      <InvoiceCard key={invoice.id} invoice={invoice} onView={setViewingInvoice} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-md)' }}>
                  <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, invoices.length)} of {invoices.length}
                  </span>
                  <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="btn btn-secondary btn-sm hide-mobile"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => p - 1)}
                      disabled={currentPage === 1}
                      className="btn btn-secondary btn-sm"
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                      .reduce((acc, page, i, arr) => {
                        if (i > 0 && page - arr[i - 1] > 1) acc.push('...');
                        acc.push(page);
                        return acc;
                      }, [])
                      .map((item, i) =>
                        item === '...' ? (
                          <span key={`ellipsis-${i}`} style={{ padding: 'var(--spacing-xs) var(--spacing-sm)', color: 'var(--color-text-secondary)' }}>...</span>
                        ) : (
                          <button
                            key={item}
                            onClick={() => setCurrentPage(item)}
                            className={`btn btn-sm ${currentPage === item ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            {item}
                          </button>
                        )
                      )}
                    <button
                      onClick={() => setCurrentPage(p => p + 1)}
                      disabled={currentPage === totalPages}
                      className="btn btn-secondary btn-sm"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="btn btn-secondary btn-sm hide-mobile"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          </>}
        </div>
      </div>

      <Footer />

      {showUploadModal && (
        <InvoiceUpload
          onClose={() => setShowUploadModal(false)}
          onRecurringCreated={() => {
            recurringListRef.current?.refresh();
            loadInvoices(); // due entries are invoiced immediately on create
          }}
        />
      )}

      {viewingInvoice && (
        <InvoiceViewModal
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
        />
      )}
    </>
  );
};

export default Dashboard;
